import { ArrowLeft, Send, CheckCircle, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState, useMemo, useEffect } from 'react';
import { useSubmitFeedbackMutation, useGetModulesQuery, useGetPublicArticlesQuery } from '../../store/apis/helpCenter.api';
import { Select, TextArea, Button } from '../common';
import { scrollToTop } from '../survey/hooks';
import toast from 'react-hot-toast';

interface HelpContactFormProps {
    onBack: () => void;
    initialModuleCode?: string;
    initialArticleId?: string;
    isLocked?: boolean;
}

export function HelpContactForm({ onBack, initialModuleCode, initialArticleId, isLocked }: HelpContactFormProps) {
    useEffect(() => {
        scrollToTop();
    }, []);

    const [submitFeedback, { isLoading }] = useSubmitFeedbackMutation();
    const { data: allModules = [], isLoading: isModulesLoading } = useGetModulesQuery();
    const { data: publicArticlesData, isLoading: isArticlesLoading } = useGetPublicArticlesQuery();

    const modules = useMemo(() => {
        if (!publicArticlesData) return [];
        return allModules.filter(m => publicArticlesData[m.code] && publicArticlesData[m.code].length > 0);
    }, [allModules, publicArticlesData]);


    const [formData, setFormData] = useState({
        moduleCode: initialModuleCode || '',
        articleId: initialArticleId || '',
        description: '',
    });

    const currentArticles = useMemo(() => {
        if (!formData.moduleCode || !publicArticlesData) return [];
        return publicArticlesData[formData.moduleCode] || [];
    }, [formData.moduleCode, publicArticlesData]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            if (!formData.moduleCode) {
                toast.error("Please select a module");
                return;
            }
            if (!formData.description) {
                toast.error("Please provide a description");
                return;
            }

            await submitFeedback({
                moduleCode: formData.moduleCode,
                articleId: formData.articleId,
                description: formData.description,
            }).unwrap();

            toast.success("Feedback submitted successfully");
            setFormData(prev => ({ ...prev, description: '' }));
        } catch (error) {
            console.error('Failed to submit feedback:', error);
            toast.error("Failed to submit feedback");
        }
    };


    return (
        <div className="p-6 max-w-full mx-auto">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
            >
                {/* Form Header */}
                <div className="mb-10 flex flex-col md:flex-row md:items-start justify-between gap-6">
                    <div className="flex-1">
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Contact Support</h1>
                    </div>
                    <Button
                        appearance="ghost"
                        onClick={onBack}
                        size="small"
                        className="text-slate-500 self-start"
                        icon={<ArrowLeft size={16} />}
                    >
                        Back
                    </Button>
                </div>

                {/* Form */}
                <div className='bg-white rounded-xl shadow-soft border border-slate-200 p-6 mb-8'>


                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Module */}
                            <div>
                                <Select
                                    label="Module"
                                    required
                                    value={formData.moduleCode}
                                    onChange={(value) => {
                                        setFormData({
                                            ...formData,
                                            moduleCode: String(value),
                                            articleId: '' // Reset article when module changes
                                        });
                                    }}
                                    options={modules.map(m => ({
                                        label: m.name,
                                        value: m.code,
                                    }))}
                                    placeholder="Select module"
                                    className='w-full'
                                    isLoading={isModulesLoading}
                                    disabled={isLocked}
                                />
                            </div>

                            {/* Article */}
                            <div>
                                <Select
                                    label="Article"
                                    value={formData.articleId}
                                    onChange={(value) => setFormData({ ...formData, articleId: String(value) })}
                                    options={currentArticles.map(a => ({
                                        label: a.title,
                                        value: a._id,
                                    }))}
                                    placeholder={formData.moduleCode ? "Select article" : "Select a module first"}
                                    className='w-full'
                                    isLoading={isArticlesLoading}
                                    disabled={isLocked || !formData.moduleCode}
                                />
                            </div>
                        </div>

                        {/* Description */}
                        <div>
                            <TextArea
                                label="Description"
                                required
                                value={formData.description}
                                onChange={(value) => setFormData({ ...formData, description: value as string })}
                                rows={10}
                                placeholder="Please provide as much detail as possible about your issue or question..."
                            />
                        </div>

                        {/* Footer Row: Buttons & Info */}

                        <div className="flex flex-row items-center gap-3 w-full justify-end">
                            <Button
                                htmlType="button"
                                appearance="secondary"
                                onClick={onBack}
                                className="w-1/3 md:w-auto"
                            >
                                Cancel
                            </Button>
                            <Button
                                htmlType="submit"
                                onClick={(e) => {
                                    // Ensure it fires even if htmlType mapping is swallowed
                                    handleSubmit(e as any);
                                }}
                                disabled={isLoading || isModulesLoading}
                                appearance="primary"
                                icon={<FileText className="w-5 h-5" />}
                                className="w-2/3 md:w-auto whitespace-nowrap flex justify-center"
                            >
                                <span className="whitespace-nowrap truncate">{isLoading ? 'Submitting...' : 'Submit Feedback'}</span>
                            </Button>
                        </div>

                        <div className="text-left border-t border-slate-50 pt-4">
                            <p className="text-sm  text-slate-900 leading-relaxed">
                                Our support team will review your feedback and get back to you shortly.
                            </p>
                        </div>

                    </form>
                </div>
            </motion.div>
        </div>
    );
}

